import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, Checkbox, Chip, CircularProgress,
  Container, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Paper, Radio, RadioGroup, Stack,
  TextField, Typography
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import PetsRoundedIcon from '@mui/icons-material/PetsRounded';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.tsx';
import { supabase } from '../supabaseClient.ts';

type Dog = {
  id:string; name:string; breed:string|null; sex:string;
  birth_date:string|null; neutered:boolean|null;
  chemically_neutered:boolean|null; care_notes:string|null;
  direct_booking_allowed:boolean; off_leash_allowed:boolean;
  swimming_allowed:boolean;
  never_in_heat:boolean|null;
  last_heat_date:string|null;
  next_heat_month:string|null;
};

type App = {
  id:string; dog_name:string; breeds:string[];
  crossbreed_unknown:boolean; status:string;
  phone_call_slots?:{starts_at:string;ends_at:string}[];
};

type Slot = {
  slot_id:string; starts_at:string; ends_at:string;
};

export const MyDogsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [dogs,setDogs] = useState<Dog[]>([]);
  const [apps,setApps] = useState<App[]>([]);
  const [loading,setLoading] = useState(true);
  const [busy,setBusy] = useState(false);
  const [error,setError] = useState('');
  const [success,setSuccess] = useState('');

  const [edit,setEdit] = useState<Dog|null>(null);
  const [name,setName] = useState('');
  const [birth,setBirth] = useState('');
  const [neutered,setNeutered] = useState(false);
  const [chemical,setChemical] = useState(false);
  const [neverInHeat,setNeverInHeat] = useState(false);
  const [lastHeat,setLastHeat] = useState('');
  const [nextHeatMonth,setNextHeatMonth] = useState('');
  const [notes,setNotes] = useState('');

  const [callApp,setCallApp] = useState<App|null>(null);
  const [slots,setSlots] = useState<Slot[]>([]);
  const [offset,setOffset] = useState(0);
  const [selected,setSelected] = useState('');
  const [slotBusy,setSlotBusy] = useState(false);

  useEffect(() => { load(); }, [user]);

  async function load() {
    if (!supabase || !user) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const { data: family } = await supabase
      .from('families')
      .select('id')
      .eq('user_id',user.id)
      .maybeSingle();

    if (!family) {
      setError('Je klantprofiel kon niet worden geladen.');
      setLoading(false);
      return;
    }

    const dogResult = await supabase
      .from('dogs')
      .select(`
        id,name,breed,sex,birth_date,neutered,
        chemically_neutered,care_notes,direct_booking_allowed,
        off_leash_allowed,swimming_allowed,
        never_in_heat,last_heat_date,next_heat_month
      `)
      .eq('family_id',family.id)
      .eq('active',true)
      .order('name');

    const appResult = await supabase
      .from('dog_applications')
      .select(`
        id,dog_name,breeds,crossbreed_unknown,status,
        phone_call_slots(starts_at,ends_at)
      `)
      .eq('family_id',family.id)
      .in('status',[
        'pending_phone_call',
        'phone_call_scheduled',
        'needs_review',
        'pending_meeting'
      ])
      .order('created_at',{ascending:false});

    if (dogResult.error || appResult.error) {
      setError('De gegevens konden niet volledig worden geladen.');
    } else {
      setDogs((dogResult.data ?? []) as Dog[]);
      setApps((appResult.data ?? []) as App[]);
    }

    setLoading(false);
  }

  function editDog(d:Dog) {
    setEdit(d);
    setName(d.name);
    setBirth(d.birth_date ?? '');
    setNeutered(d.neutered ?? false);
    setChemical(d.sex === 'reu' ? (d.chemically_neutered ?? false) : false);
    setNeverInHeat(d.sex === 'teef' ? (d.never_in_heat ?? false) : false);
    setLastHeat(d.sex === 'teef' ? (d.last_heat_date ?? '') : '');
    setNextHeatMonth(
      d.sex === 'teef' && d.next_heat_month
        ? d.next_heat_month.slice(0,7)
        : ''
    );
    setNotes(d.care_notes ?? '');
    setError('');
    setSuccess('');
  }

  async function saveDog() {
    if (!supabase || !edit) return;

    setBusy(true);
    setError('');

    const { error } = await supabase.rpc('update_customer_dog_v2',{
      p_dog_id:edit.id,
      p_name:name,
      p_birth_date:birth || null,
      p_neutered:neutered,
      p_chemically_neutered:
        edit.sex === 'reu' ? chemical : false,
      p_care_notes:notes,
      p_never_in_heat:
        edit.sex === 'teef' && !neutered ? neverInHeat : false,
      p_last_heat_date:
        edit.sex === 'teef' && !neutered && !neverInHeat
          ? (lastHeat || null)
          : null,
      p_next_heat_month:
        edit.sex === 'teef' && !neutered && nextHeatMonth
          ? `${nextHeatMonth}-01`
          : null
    });

    if (error) {
      setError(error.message);
    } else {
      setSuccess('De gegevens van je hond zijn opgeslagen.');
      setEdit(null);
      await load();
    }

    setBusy(false);
  }

  async function loadSlots(newOffset:number) {
    if (!supabase) return;

    setSlotBusy(true);
    setSelected('');

    const { data,error } = await supabase.rpc(
      'next_phone_call_slots',
      { p_limit:3, p_offset:newOffset }
    );

    if (error) {
      setError('De beschikbare belmomenten konden niet worden geladen.');
    } else {
      setSlots((data ?? []) as Slot[]);
      setOffset(newOffset);
    }

    setSlotBusy(false);
  }

  async function openPlanning(app:App) {
    setCallApp(app);
    setSlots([]);
    setSelected('');
    setOffset(0);
    setError('');
    setSuccess('');
    await loadSlots(0);
  }

  function closePlanning() {
    if (slotBusy) return;
    setCallApp(null);
    setSlots([]);
    setSelected('');
    setOffset(0);
  }

  async function bookCall() {
    if (!supabase || !callApp || !selected) return;

    setSlotBusy(true);

    const { error } = await supabase.rpc(
      'book_customer_phone_call',
      {
        p_application_id:callApp.id,
        p_slot_id:selected
      }
    );

    if (error) {
      setError(error.message);
      setSlotBusy(false);
      return;
    }

    setSuccess('Je telefonische kennismaking is ingepland.');
    setCallApp(null);
    setSlots([]);
    setSelected('');
    setOffset(0);
    setSlotBusy(false);
    await load();
  }

  const yesNo = (v:boolean|null) =>
    v === null ? 'Niet ingevuld' : v ? 'Ja' : 'Nee';

  const monthLabel = (v:string|null) => {
    if (!v) return 'Niet ingevuld';
    return new Intl.DateTimeFormat('nl-NL',{
      month:'long',year:'numeric'
    }).format(new Date(`${v.slice(0,7)}-01T12:00:00`));
  };

  const dogDate = (v:string) =>
    new Intl.DateTimeFormat('nl-NL',{
      weekday:'long',day:'numeric',month:'long',year:'numeric'
    }).format(new Date(`${v}T12:00:00`));

  const callDate = (v:string) =>
    new Intl.DateTimeFormat('nl-NL',{
      weekday:'long',day:'numeric',month:'long',
      hour:'2-digit',minute:'2-digit'
    }).format(new Date(v));

  const time = (v:string) =>
    new Intl.DateTimeFormat('nl-NL',{
      hour:'2-digit',minute:'2-digit'
    }).format(new Date(v));

  const status = (a:App) => {
    if (a.status === 'phone_call_scheduled')
      return 'Telefonische kennismaking gepland';
    if (a.status === 'needs_review')
      return 'In beoordeling bij Pippaya';
    if (a.status === 'pending_meeting')
      return 'Persoonlijke kennismaking';
    return 'Telefonische kennismaking nog te plannen';
  };

  if (loading) {
    return (
      <Box sx={{p:6,textAlign:'center'}}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{minHeight:'100vh',bgcolor:'#fffafb',py:4}}>
      <Container maxWidth="md">
        <Button
          startIcon={<ArrowBackRoundedIcon />}
          onClick={() => navigate('/mijn-pippaya')}
        >
          Terug naar Mijn Pippaya
        </Button>

        <Typography variant="overline" color="text.secondary">
          Mijn Pippaya
        </Typography>

        <Typography variant="h3">Mijn honden</Typography>

        <Typography color="text.secondary" sx={{mt:1}}>
          Bekijk en wijzig de gegevens van je hond(en).
        </Typography>

        {error && <Alert severity="error" sx={{mt:3}}>{error}</Alert>}
        {success && <Alert severity="success" sx={{mt:3}}>{success}</Alert>}

        <Stack spacing={2} sx={{mt:3}}>
          {dogs.map(d => (
            <Card key={d.id} sx={{p:2.5}}>
              <Stack
                direction={{xs:'column',sm:'row'}}
                justifyContent="space-between"
                gap={2}
              >
                <Stack direction="row" spacing={2}>
                  <PetsRoundedIcon />
                  <Box>
                    <Typography variant="h5">{d.name}</Typography>
                    <Typography color="text.secondary">
                      {d.breed || 'Ras niet ingevuld'}
                    </Typography>
                    <Typography variant="body2" sx={{mt:1}}>
                      Geslacht: <strong>{d.sex}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Geboortedatum:{' '}
                      <strong>
                        {d.birth_date ? dogDate(d.birth_date) : 'Niet ingevuld'}
                      </strong>
                    </Typography>
                    <Typography variant="body2">
                      Gecastreerd/gesteriliseerd:{' '}
                      <strong>{yesNo(d.neutered)}</strong>
                    </Typography>
                    {d.sex === 'reu' && (
                      <Typography variant="body2">
                        Chemisch gecastreerd:{' '}
                        <strong>{yesNo(d.chemically_neutered)}</strong>
                      </Typography>
                    )}

                    {d.sex === 'teef' && d.neutered === false && (
                      <>
                        <Typography variant="body2">
                          Loopsheid:{' '}
                          <strong>
                            {d.never_in_heat
                              ? 'Nog niet loops geweest'
                              : d.last_heat_date
                                ? `Laatste loopsheid ${dogDate(d.last_heat_date)}`
                                : 'Laatste loopsheid niet ingevuld'}
                          </strong>
                        </Typography>

                        <Typography variant="body2">
                          Verwachte volgende loopsheid:{' '}
                          <strong>{monthLabel(d.next_heat_month)}</strong>
                        </Typography>
                      </>
                    )}
                  </Box>
                </Stack>

                <Button
                  startIcon={<EditRoundedIcon />}
                  onClick={() => editDog(d)}
                >
                  Gegevens wijzigen
                </Button>
              </Stack>

              {d.care_notes && (
                <Alert severity="info" sx={{mt:2}}>
                  <strong>Verzorging & bijzonderheden:</strong>
                  <br />
                  {d.care_notes}
                </Alert>
              )}

              <Stack
                direction={{xs:'column',sm:'row'}}
                spacing={1}
                sx={{mt:2}}
              >
                <Chip
                  size="small"
                  color={d.direct_booking_allowed ? 'success' : 'default'}
                  label={
                    d.direct_booking_allowed
                      ? 'Direct boeken toegestaan'
                      : 'Boeken na overleg'
                  }
                />
                <Chip
                  size="small"
                  label={
                    d.off_leash_allowed
                      ? 'Loslopen toegestaan'
                      : 'Niet loslopen'
                  }
                />
                <Chip
                  size="small"
                  label={
                    d.swimming_allowed
                      ? 'Zwemmen toegestaan'
                      : 'Niet zwemmen'
                  }
                />
              </Stack>

              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{mt:1.5}}
              >
                Direct boeken, loslopen en zwemmen worden door Pippaya beheerd.
              </Typography>
            </Card>
          ))}
        </Stack>

        {apps.length > 0 && (
          <Card sx={{p:2.5,mt:3}}>
            <Typography variant="h5">Lopende aanmeldingen</Typography>
            <Typography color="text.secondary" sx={{mt:.5}}>
              Honden die nog in de kennismakingsprocedure zitten.
            </Typography>

            <Stack spacing={1.5} sx={{mt:2}}>
              {apps.map(a => {
                const call = a.phone_call_slots?.[0];

                return (
                  <Card
                    key={a.id}
                    variant="outlined"
                    sx={{p:2,boxShadow:'none'}}
                  >
                    <Stack
                      direction={{xs:'column',sm:'row'}}
                      justifyContent="space-between"
                      gap={2}
                    >
                      <Box flex={1}>
                        <Typography fontWeight={800}>{a.dog_name}</Typography>

                        <Typography color="text.secondary">
                          {a.crossbreed_unknown
                            ? 'Kruising · afkomst onbekend'
                            : a.breeds?.join(' × ') || 'Ras in beoordeling'}
                        </Typography>

                        {call && (
                          <Typography variant="body2" sx={{mt:1}}>
                            Belafspraak:{' '}
                            <strong>
                              {callDate(call.starts_at)} – {time(call.ends_at)}
                            </strong>
                          </Typography>
                        )}

                        {a.status === 'pending_phone_call' ? (
                          <>
                            <Typography variant="body2" sx={{mt:1}}>
                              Kies een telefonische kennismaking.
                            </Typography>

                            <Button
                              variant="contained"
                              size="small"
                              sx={{mt:1.5}}
                              onClick={() => openPlanning(a)}
                            >
                              Belmoment kiezen
                            </Button>
                          </>
                        ) : (
                          <Typography variant="body2" sx={{mt:1}}>
                            Na de kennismaking beoordeelt Pippaya of opvang
                            mogelijk is.
                          </Typography>
                        )}
                      </Box>

                      <Chip
                        size="small"
                        color="warning"
                        label={status(a)}
                      />
                    </Stack>
                  </Card>
                );
              })}
            </Stack>
          </Card>
        )}
      </Container>

      <Dialog
        open={!!edit}
        onClose={() => !busy && setEdit(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Gegevens hond wijzigen</DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{mt:1}}>
            <TextField
              label="Naam"
              value={name}
              onChange={e => setName(e.target.value)}
              required
            />

            <TextField
              label="Ras"
              value={edit?.breed ?? ''}
              InputProps={{readOnly:true}}
              helperText="Ras wordt door Pippaya beheerd. Klopt dit niet, neem dan even contact op."
            />

            <TextField
              label="Geslacht"
              value={
                edit?.sex === 'reu'
                  ? 'Reu'
                  : edit?.sex === 'teef'
                    ? 'Teef'
                    : edit?.sex ?? ''
              }
              InputProps={{readOnly:true}}
              helperText="Geslacht wordt door Pippaya beheerd."
            />

            <TextField
              type="date"
              label="Geboortedatum"
              value={birth}
              onChange={e => setBirth(e.target.value)}
              InputLabelProps={{shrink:true}}
              inputProps={{max:new Date().toISOString().slice(0,10)}}
            />

            {edit?.sex === 'reu' ? (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={neutered}
                      onChange={e => setNeutered(e.target.checked)}
                    />
                  }
                  label="Gecastreerd"
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={chemical}
                      onChange={e => setChemical(e.target.checked)}
                    />
                  }
                  label="Chemisch gecastreerd"
                />
              </>
            ) : (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={neutered}
                      onChange={e => setNeutered(e.target.checked)}
                    />
                  }
                  label="Gesteriliseerd"
                />

                {!neutered && (
                  <>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={neverInHeat}
                          onChange={e => setNeverInHeat(e.target.checked)}
                        />
                      }
                      label="Nog niet loops geweest"
                    />

                    {!neverInHeat && (
                      <TextField
                        type="date"
                        label="Laatste loopsheid"
                        value={lastHeat}
                        onChange={e => setLastHeat(e.target.value)}
                        InputLabelProps={{shrink:true}}
                        inputProps={{max:new Date().toISOString().slice(0,10)}}
                        helperText="Laat leeg als je de datum niet weet."
                      />
                    )}

                    <TextField
                      type="month"
                      label="Verwachte volgende loopsheid"
                      value={nextHeatMonth}
                      onChange={e => setNextHeatMonth(e.target.value)}
                      InputLabelProps={{shrink:true}}
                      helperText="Laat leeg als je dit niet weet."
                    />
                  </>
                )}
              </>
            )}

            <TextField
              label="Verzorging & bijzonderheden"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              multiline
              minRows={4}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button disabled={busy} onClick={() => setEdit(null)}>
            Annuleren
          </Button>
          <Button
            variant="contained"
            disabled={busy || !name.trim()}
            onClick={saveDog}
          >
            {busy ? 'Opslaan...' : 'Opslaan'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!callApp}
        onClose={closePlanning}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Telefonische kennismaking plannen</DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{mt:1}}>
            {callApp && (
              <Alert severity="info">
                Kies een belmoment voor <strong>{callApp.dog_name}</strong>.
              </Alert>
            )}

            {slots.length === 0 && !slotBusy && (
              <Alert severity="info">
                Er zijn geen verdere vrije belmomenten beschikbaar.
                Pippaya neemt contact met je op.
              </Alert>
            )}

            {slotBusy && slots.length === 0 && (
              <Box sx={{textAlign:'center',py:2}}>
                <CircularProgress size={28} />
              </Box>
            )}

            {slots.length > 0 && (
              <RadioGroup
                value={selected}
                onChange={e => setSelected(e.target.value)}
              >
                {slots.map(s => (
                  <Paper
                    key={s.slot_id}
                    variant="outlined"
                    sx={{p:1.25,mb:1,boxShadow:'none'}}
                  >
                    <FormControlLabel
                      value={s.slot_id}
                      control={<Radio />}
                      label={`${callDate(s.starts_at)} – ${time(s.ends_at)}`}
                    />
                  </Paper>
                ))}
              </RadioGroup>
            )}

            <Stack direction={{xs:'column',sm:'row'}} spacing={1}>
              {offset > 0 && (
                <Button
                  variant="outlined"
                  disabled={slotBusy}
                  onClick={() => loadSlots(Math.max(0,offset - 3))}
                >
                  Vorige momenten
                </Button>
              )}

              {slots.length === 3 && (
                <Button
                  variant="outlined"
                  disabled={slotBusy}
                  onClick={() => loadSlots(offset + 3)}
                >
                  Andere momenten bekijken
                </Button>
              )}
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button disabled={slotBusy} onClick={closePlanning}>
            Annuleren
          </Button>

     <Button
  variant="contained"
  disabled={!selected || slotBusy}
  onClick={bookCall}
>
  {slotBusy ? 'Vastleggen...' : 'Belmoment vastleggen'}
</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};     